import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Calendar, DollarSign, Settings, LogOut, Palette, TrendingUp, Wallet, Tag, Building2, Users, QrCode, Ticket, CreditCard, Shield, Banknote } from 'lucide-react';
import BookingsManagement from './BookingsManagement';
import ClassesManagement from './ClassesManagement';
import EventsManagement from './EventsManagement';
import EventGuests from './EventGuests';
import PassManagement from './PassManagement';
import StudiosManagement from './StudiosManagement';
import PaymentSettings from './PaymentSettings';
import AppearanceSettings from './AppearanceSettings';
import StudioPricingSettings from './StudioPricingSettings';
import ParticipantsSummary from './ParticipantsSummary';
import ScanCheckIn from './ScanCheckIn';
import TeamManagement from './TeamManagement';
import Payroll from './Payroll';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const peso = (n) => `\u20B1${Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

// Nav items with the roles allowed to see them
const NAV_ITEMS = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard', roles: ['owner'] },
  { to: '/admin/bookings', label: 'Bookings', icon: Calendar, testid: 'nav-bookings', roles: ['owner', 'studio_manager'] },
  { to: '/admin/participants', label: 'Participants', icon: Users, testid: 'nav-participants', roles: ['owner', 'studio_manager'] },
  { to: '/admin/payroll', label: 'Payroll', icon: Banknote, testid: 'nav-payroll', roles: ['owner'] },
  { to: '/admin/event-guests', label: 'Event Guests', icon: Ticket, testid: 'nav-event-guests', roles: ['owner', 'studio_manager', 'event_operator'] },
  { to: '/admin/checkin', label: 'Check-In', icon: QrCode, testid: 'nav-checkin', roles: ['owner', 'studio_manager', 'event_operator'] },
  { to: '/admin/classes', label: 'Classes', icon: Calendar, testid: 'nav-classes', roles: ['owner', 'studio_manager'] },
  { to: '/admin/events', label: 'Events', icon: Ticket, testid: 'nav-events', roles: ['owner', 'studio_manager'] },
  { to: '/admin/class-passes', label: 'Class Passes', icon: CreditCard, testid: 'nav-class-passes', roles: ['owner'] },
  { to: '/admin/studios', label: 'Studios', icon: Building2, testid: 'nav-studios', roles: ['owner'] },
  { to: '/admin/payment-settings', label: 'Payment Settings', icon: Settings, testid: 'nav-payment-settings', roles: ['owner'] },
  { to: '/admin/studio-pricing', label: 'Studio Pricing', icon: Tag, testid: 'nav-studio-pricing', roles: ['owner'] },
  { to: '/admin/appearance', label: 'Appearance', icon: Palette, testid: 'nav-appearance', roles: ['owner'] },
  { to: '/admin/team', label: 'Team & Access', icon: Shield, testid: 'nav-team', roles: ['owner'] },
];

const DEFAULT_LANDING = { owner: '/admin/dashboard', studio_manager: '/admin/classes', event_operator: '/admin/checkin' };

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [role, setRole] = useState(null);
  const [stats, setStats] = useState({
    total_bookings: 0, pending_payments: 0, confirmed_bookings: 0, rejected_bookings: 0,
    total_revenue: 0, revenue_this_week: 0, revenue_this_month: 0, pending_revenue: 0, breakdown: []
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const me = await axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } });
      const r = me.data.role || 'owner';
      setRole(r);
      // Redirect non-owners away from the base/dashboard to their landing page
      if ((location.pathname === '/admin' || location.pathname === '/admin/dashboard') && r !== 'owner') {
        navigate(DEFAULT_LANDING[r] || '/admin/checkin', { replace: true });
      }
      if (r === 'owner') fetchStats();
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;
  const visibleNav = role ? NAV_ITEMS.filter((i) => i.roles.includes(role)) : [];

  // Route guard: redirect a role to its landing page if it hits a URL it's not allowed to view
  useEffect(() => {
    if (!role) return;
    const match = NAV_ITEMS.find((i) => i.to === location.pathname);
    if (match && !match.roles.includes(role)) {
      navigate(DEFAULT_LANDING[role] || '/admin/checkin', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, location.pathname]);

  return (
    <div className="min-h-screen flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <div className="w-64 bg-black text-white p-6 flex flex-col">
        <div className="mb-8">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
            ADMIN PANEL
          </h1>
          <p className="text-zinc-400 text-sm">Studio at the Third</p>
          {role && role !== 'owner' && (
            <span className="inline-block mt-2 text-xs bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded-full" data-testid="role-badge">
              {role === 'studio_manager' ? 'Studio Manager' : 'Event Operator'}
            </span>
          )}
        </div>

        <nav className="flex-1 space-y-2">
          {visibleNav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`flex items-center gap-3 px-4 py-3 rounded transition-colors ${
                isActive(item.to) ? 'bg-[var(--accent)] text-black font-bold' : 'hover:bg-zinc-800'
              }`}
              data-testid={item.testid}
            >
              <item.icon size={20} />
              {item.label}
            </Link>
          ))}
        </nav>

        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 rounded transition-colors"
          data-testid="logout-btn"
        >
          <LogOut size={20} />
          Logout
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-zinc-50">
        <Routes>
          <Route
            path="dashboard"
            element={
              <div className="p-8">
                <h2 className="text-3xl font-bold mb-8" style={{ fontFamily: 'var(--heading-font)' }} data-testid="dashboard-title">
                  Dashboard Overview
                </h2>

                {/* Revenue cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-testid="revenue-cards">
                  <div className="bg-black text-white p-6" data-testid="stat-total-revenue">
                    <div className="flex items-center justify-between mb-4">
                      <Wallet size={32} className="text-[var(--accent)]" />
                      <span className="text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
                        {peso(stats.total_revenue)}
                      </span>
                    </div>
                    <p className="text-zinc-300 font-medium">Confirmed Revenue (All-time)</p>
                  </div>
                  <div className="bg-white border-2 border-black p-6" data-testid="stat-revenue-week">
                    <div className="flex items-center justify-between mb-4">
                      <TrendingUp size={32} />
                      <span className="text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
                        {peso(stats.revenue_this_week)}
                      </span>
                    </div>
                    <p className="text-zinc-600 font-medium">Revenue This Week</p>
                  </div>
                  <div className="bg-white border-2 border-black p-6" data-testid="stat-revenue-month">
                    <div className="flex items-center justify-between mb-4">
                      <TrendingUp size={32} />
                      <span className="text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
                        {peso(stats.revenue_this_month)}
                      </span>
                    </div>
                    <p className="text-zinc-600 font-medium">Revenue This Month</p>
                  </div>
                </div>

                {/* Booking count cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                  <div className="bg-white border-2 border-black p-6" data-testid="stat-total-bookings">
                    <div className="flex items-center justify-between mb-4">
                      <Calendar size={28} />
                      <span className="text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
                        {stats.total_bookings}
                      </span>
                    </div>
                    <p className="text-zinc-600 font-medium">Total Bookings</p>
                  </div>

                  <div className="bg-white border-2 border-[var(--accent)] p-6" data-testid="stat-pending-payments">
                    <div className="flex items-center justify-between mb-4">
                      <DollarSign size={28} />
                      <span className="text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
                        {stats.pending_payments}
                      </span>
                    </div>
                    <p className="text-zinc-600 font-medium">Pending ({peso(stats.pending_revenue)})</p>
                  </div>

                  <div className="bg-white border-2 border-green-500 p-6" data-testid="stat-confirmed-bookings">
                    <div className="flex items-center justify-between mb-4">
                      <Calendar size={28} className="text-green-600" />
                      <span className="text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
                        {stats.confirmed_bookings}
                      </span>
                    </div>
                    <p className="text-zinc-600 font-medium">Confirmed</p>
                  </div>

                  <div className="bg-white border-2 border-red-500 p-6" data-testid="stat-rejected-bookings">
                    <div className="flex items-center justify-between mb-4">
                      <Calendar size={28} className="text-red-600" />
                      <span className="text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
                        {stats.rejected_bookings}
                      </span>
                    </div>
                    <p className="text-zinc-600 font-medium">Rejected</p>
                  </div>
                </div>

                {/* Revenue breakdown */}
                <div className="bg-white border border-zinc-200" data-testid="revenue-breakdown">
                  <h3 className="text-xl font-bold p-6 pb-4" style={{ fontFamily: 'var(--heading-font)' }}>
                    Income by Class / Studio (Confirmed)
                  </h3>
                  {stats.breakdown && stats.breakdown.length > 0 ? (
                    <table className="w-full">
                      <thead className="bg-zinc-100 border-y border-zinc-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-sm font-bold">Name</th>
                          <th className="px-6 py-3 text-left text-sm font-bold">Type</th>
                          <th className="px-6 py-3 text-right text-sm font-bold">Bookings</th>
                          <th className="px-6 py-3 text-right text-sm font-bold">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.breakdown.map((item) => (
                          <tr key={item.name} className="border-b border-zinc-100" data-testid={`breakdown-row-${item.name}`}>
                            <td className="px-6 py-3 font-medium">{item.name}</td>
                            <td className="px-6 py-3 capitalize text-zinc-600">{item.type}</td>
                            <td className="px-6 py-3 text-right">{item.count}</td>
                            <td className="px-6 py-3 text-right font-bold">{peso(item.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="px-6 pb-6 text-zinc-500" data-testid="breakdown-empty">No confirmed revenue yet.</p>
                  )}
                </div>
              </div>
            }
          />
          <Route path="bookings" element={<BookingsManagement />} />
          <Route path="participants" element={<ParticipantsSummary />} />
          <Route path="payroll" element={<Payroll />} />
          <Route path="event-guests" element={<EventGuests />} />
          <Route path="checkin" element={<ScanCheckIn />} />
          <Route path="classes" element={<ClassesManagement />} />
          <Route path="events" element={<EventsManagement />} />
          <Route path="class-passes" element={<PassManagement />} />
          <Route path="team" element={<TeamManagement />} />
          <Route path="studios" element={<StudiosManagement />} />
          <Route path="payment-settings" element={<PaymentSettings />} />
          <Route path="studio-pricing" element={<StudioPricingSettings />} />
          <Route path="appearance" element={<AppearanceSettings />} />
        </Routes>
      </div>
    </div>
  );
};

export default AdminDashboard;